import { Link } from "react-router-dom";
import styled from "styled-components";
import { AppButton } from "@/shared/components";
import { mediaPhone } from "@/shared/theme/breakpoints";
import { pressedTint } from "@/shared/theme/pressed";

const Content = styled.div`
	display: flex;
	flex-direction: column;
	gap: 35px;
`;

const PersonaGrid = styled.div`
	display: grid;
	grid-template-columns: repeat(2, minmax(0, 1fr));
	grid-template-rows: repeat(2, 160px);
	column-gap: 12px;
	row-gap: 8px;
	width: 100%;

	/* Cards size to their content on narrow screens instead of fixed 160px rows. */
	${mediaPhone`grid-template-rows: auto;`}
`;

const PersonaCard = styled.button<{ $selected?: boolean; $disabled?: boolean }>`
	box-sizing: border-box;
	border: 2px solid ${({ $selected }) => ($selected ? "#4460ef" : "#cdd4f6")};
	border-radius: 16px;
	padding: 8px;
	background: ${({ $disabled, $selected }) => ($disabled ? "#f6f6f6" : $selected ? "#fdfdfd" : "#fefefe")};
	box-shadow: ${({ $selected }) => ($selected ? "0 4px 12px rgba(21, 89, 165, 0.24)" : "none")};
	display: flex;
	flex-direction: column;
	align-items: center;
	overflow: hidden;
	font: inherit;
	cursor: ${({ $disabled }) => ($disabled ? "not-allowed" : "pointer")};
	transition:
		border-color 120ms ease-out,
		background-color 120ms ease-out,
		box-shadow 120ms ease-out;

	&:not(:disabled):hover,
	&:not(:disabled):focus-visible {
		border-color: ${({ $selected }) => ($selected ? "#4460ef" : "transparent")};
		background: ${({ $selected }) => ($selected ? "#fdfdfd" : "#ebeefd")};
	}

	&:focus-visible {
		outline: 2px solid #3a0ca3;
		outline-offset: 2px;
	}

	${pressedTint}
`;

const Illustration = styled.div<{ $disabled?: boolean; $selected?: boolean }>`
	width: 100%;
	height: 64px;
	border-radius: 8px;
	background: ${({ $selected }) => ($selected ? "rgba(68, 96, 239, 0.08)" : "rgba(27, 2, 85, 0.04)")};
	display: flex;
	align-items: flex-end;
	justify-content: center;
	overflow: hidden;
	transition: background-color 120ms ease-out;

	img {
		width: auto;
		height: 64px;
		max-width: 100%;
		object-fit: contain;
		filter: ${({ $disabled }) => ($disabled ? "grayscale(1)" : "none")};
	}
`;

const PersonaCopy = styled.div`
	display: flex;
	flex: 1;
	flex-direction: column;
	align-items: center;
	justify-content: center;
	gap: 8px;
	padding: 0 8px;
	text-align: center;
	opacity: 0.8;
`;

const PersonaTitle = styled.strong`
	color: #303030;
	font-size: 14px;
	font-weight: 600;
	line-height: normal;
`;

const PersonaDescription = styled.span`
	color: #707070;
	font-size: 10px;
	font-weight: 500;
	line-height: normal;
`;

const Actions = styled.div`
	display: flex;
	flex-direction: column;
	align-items: center;
	gap: 12px;
`;

const NextButton = styled(AppButton)`
	width: 100%;
	height: 47px;
	border-radius: 64px;
	font-size: 14px;
	line-height: 24px;

	&:disabled {
		background: #d3d3d3;
		color: #b0b0b0;
		cursor: not-allowed;
	}
`;

const LoginPrompt = styled.div`
	font-size: 14px;
	line-height: 24px;
	color: #303030;

	a {
		color: #3a0ca3;
		font-weight: 600;
	}
`;

export type RegistrationRole = "creator" | "client" | "agency";

interface RoleSelectorStepProps {
	selected: RegistrationRole | null;
	onSelect: (persona: RegistrationRole) => void;
	onContinue: () => void;
}

export const RoleSelectorStep = ({ selected, onSelect, onContinue }: RoleSelectorStepProps) => (
	<Content>
		<PersonaGrid role="group" aria-label="Choose how you use Creatorverse">
			<PersonaCard
				type="button"
				$selected={selected === "creator"}
				aria-pressed={selected === "creator"}
				onPointerDown={() => selected !== "creator" && onSelect("creator")}
				onClick={() => onSelect("creator")}
			>
				<Illustration $selected={selected === "creator"}>
					<img src="/assets/creator-register/creator-role.svg" alt="" />
				</Illustration>
				<PersonaCopy>
					<PersonaTitle>A Creator</PersonaTitle>
					<PersonaDescription>You create content and run your own show.</PersonaDescription>
				</PersonaCopy>
			</PersonaCard>
			<PersonaCard
				type="button"
				$selected={selected === "client"}
				aria-pressed={selected === "client"}
				onPointerDown={() => selected !== "client" && onSelect("client")}
				onClick={() => onSelect("client")}
			>
				<Illustration $selected={selected === "client"}>
					<img src="/assets/creator-register/client-role.svg" alt="" />
				</Illustration>
				<PersonaCopy>
					<PersonaTitle>A Client</PersonaTitle>
					<PersonaDescription>You’re a brand working on campaigns with us.</PersonaDescription>
				</PersonaCopy>
			</PersonaCard>
			<PersonaCard
				type="button"
				$selected={selected === "agency"}
				aria-pressed={selected === "agency"}
				onPointerDown={() => selected !== "agency" && onSelect("agency")}
				onClick={() => onSelect("agency")}
			>
				<Illustration $selected={selected === "agency"}>
					<img src="/assets/creator-register/agency-role.svg" alt="" />
				</Illustration>
				<PersonaCopy>
					<PersonaTitle>An Agency</PersonaTitle>
					<PersonaDescription>Registered Agency Users only</PersonaDescription>
				</PersonaCopy>
			</PersonaCard>
			<PersonaCard type="button" $disabled aria-label="Talent Manager registration coming soon" disabled>
				<Illustration $disabled>
					<img src="/assets/creator-register/talent-manager-role.svg" alt="" />
				</Illustration>
				<PersonaCopy>
					<PersonaTitle>Talent Manager</PersonaTitle>
					<PersonaDescription>You represent and grow creator talent.</PersonaDescription>
				</PersonaCopy>
			</PersonaCard>
		</PersonaGrid>

		<Actions>
			<NextButton type="button" variant="primary" disabled={!selected} onClick={onContinue}>
				Next
			</NextButton>
			<LoginPrompt>
				Already have an account? <Link to="/login">Log in</Link>
			</LoginPrompt>
		</Actions>
	</Content>
);
